import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Container,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import SRCards from "../../components/SRCards";
import SelectCompany from "../../components/SelectCompany";
import LoadingModal from "../../components/LoadingModal";
import EstimateApi from "../../services/estimate.service";
import { CHECK_VALUE } from "../../common/data-validator";

type RejectionReason = {
    id?: number;
    reason?: string;
    reasonType?: string;
    createdAt?: string;
    [key: string]: any;
};

type SnackbarSeverity = "success" | "error" | "info" | "warning";

const MAX_REASON_LENGTH = 256;

const useStyles = makeStyles({
    container: {
        marginTop: "24px",
        marginBottom: "24px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "16px",
        marginBottom: "16px",
        gap: "12px",
        flexWrap: "wrap",
    },
    formRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        marginTop: "12px",
    },
    textField: {
        minWidth: "320px",
        flexGrow: 1,
    },
    tableWrapper: {
        marginTop: "16px",
    },
    emptyCell: {
        textAlign: "center",
    },
});

const EstimateRejectionReasons = () => {
    const classes = useStyles();
    const apiservices = useMemo(() => new EstimateApi(), []);

    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [reasons, setReasons] = useState<RejectionReason[]>([]);
    const [featureEnabled, setFeatureEnabled] = useState(false);
    const [newReason, setNewReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: SnackbarSeverity;
    }>({ open: false, message: "", severity: "success" });

    const showSnackbar = (message: string, severity: SnackbarSeverity) => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchReasons = useCallback(async () => {
        if (!CHECK_VALUE(selectedCompany?.value)) {
            return;
        }
        setLoading(true);
        try {
            const result: any = await apiservices.fetchEstimateRejectionReasons(
                {}
            );
            if (!result || typeof result === "string") {
                throw new Error("Failed to fetch reasons");
            }
            setFeatureEnabled(Boolean(result.enabled));
            setReasons(Array.isArray(result.reasons) ? result.reasons.filter((r: RejectionReason) => r.reasonType !== "CUSTOM") : []);
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to fetch estimate rejection reasons", "error");
            setFeatureEnabled(false);
            setReasons([]);
        } finally {
            setLoading(false);
        }
    }, [apiservices, selectedCompany]);

    useEffect(() => {
        fetchReasons();
    }, [fetchReasons]);

    const handleAddReason = async () => {
        const trimmedReason = newReason.trim();
        if (!featureEnabled) {
            showSnackbar(
                "Enable Estimate Rejection Reasons before adding custom reasons",
                "error"
            );
            return;
        }
        if (!CHECK_VALUE(trimmedReason)) {
            showSnackbar("Enter a rejection reason", "error");
            return;
        }
        if (trimmedReason.length > MAX_REASON_LENGTH) {
            showSnackbar(
                `Reason must be ${MAX_REASON_LENGTH} characters or fewer`,
                "error"
            );
            return;
        }
        setLoading(true);
        try {
            const payload = { reason: trimmedReason, reasonType: "STANDARD" };
            const result: any = await apiservices.addEstimateRejectionReason(
                payload
            );
            if (!result || typeof result === "string") {
                throw new Error("Failed to add reason");
            }
            setNewReason("");
            showSnackbar("Rejection reason added", "success");
            await fetchReasons();
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to add rejection reason", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SRCards />
            <Container className={classes.container}>
                <SelectCompany optionHandler={setSelectedCompany} />

                <div className={classes.headerRow}>
                    <Typography variant="h6">
                        Estimate Rejection Reasons
                    </Typography>
                </div>

                {!featureEnabled && (
                    <Alert severity="info">
                        Estimate rejection analysis is disabled for this
                        company. Enable "Estimate Rejection Reasons" in Feature
                        Enabling to manage reasons.
                    </Alert>
                )}

                <div className={classes.formRow}>
                    <TextField
                        className={classes.textField}
                        label="New rejection reason"
                        variant="outlined"
                        value={newReason}
                        onChange={(event) =>
                            setNewReason(event.target.value)
                        }
                        helperText={`${newReason.length}/${MAX_REASON_LENGTH}`}
                        disabled={!featureEnabled}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAddReason}
                        disabled={!featureEnabled || !newReason.trim()}
                    >
                        Add Reason
                    </Button>
                </div>

                <TableContainer component={Paper} className={classes.tableWrapper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Reason</TableCell>
                                <TableCell>Type</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reasons.length > 0 ? (
                                reasons.map((reason, index) => (
                                    <TableRow
                                        key={
                                            reason.id ??
                                            reason.reason ??
                                            `reason-${index}`
                                        }
                                    >
                                        <TableCell>
                                            {reason.reason || "-"}
                                        </TableCell>
                                        <TableCell>
                                            {reason.reasonType || "-"}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        className={classes.emptyCell}
                                        colSpan={2}
                                    >
                                        No rejection reasons found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                >
                    <Alert
                        onClose={() =>
                            setSnackbar((prev) => ({ ...prev, open: false }))
                        }
                        severity={snackbar.severity}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <LoadingModal openState={loading} />
            </Container>
        </>
    );
};

export default EstimateRejectionReasons;
